// ============================================================================
// API Hooks — React hooks wrapping API calls with loading/error states
// ============================================================================

import { useState, useCallback, useEffect } from 'react';
import { api, ApiError } from '../services/api';
import type {
  CaptionTemplate,
  ContentItem,
  Draft,
  JobStatus,
  PaginatedResponse,
  PlatformConfig,
} from '../types';

let captionTemplatesCache: CaptionTemplate[] | null = null;

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// ── Content Hooks ─────────────────────────────────────────────────

export function useContentList(params?: { status?: string }) {
  const [state, setState] = useState<UseApiState<PaginatedResponse<ContentItem>>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchContent = useCallback(async (silent = false) => {
    if (!silent) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }
    try {
      const data = await api.listContent(params);
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load content';
      if (silent) {
        setState((prev) => ({ ...prev, loading: false, error: message }));
      } else {
        setState({ data: null, loading: false, error: message });
      }
    }
  }, [params?.status]);

  useEffect(() => {
    fetchContent(false);
  }, [fetchContent]);

  const refetch = useCallback(() => fetchContent(true), [fetchContent]);

  return { ...state, refetch };
}

export function useContent(contentId: string | undefined) {
  const [state, setState] = useState<UseApiState<ContentItem & { drafts: Draft[] }>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchContent = useCallback(async (silent = false) => {
    if (!contentId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    if (!silent) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }
    try {
      const newData = await api.getContent(contentId);

      if (silent) {
        // Smart merge: preserve pre-signed download URLs when nothing
        // structural changed so <video> elements don't remount.
        setState((prev) => {
          if (!prev.data) {
            return { data: newData, loading: false, error: null };
          }

          const prevData = prev.data;

          // Detect structural changes that should force a full URL refresh
          const statusChanged = prevData.status !== newData.status;
          const clipStatusChanged = prevData.clipStatus !== newData.clipStatus;
          const clipsCountChanged =
            (prevData.clipsArray?.length ?? 0) !== (newData.clipsArray?.length ?? 0);
          const draftsChanged =
            (prevData.drafts?.length ?? 0) !== (newData.drafts?.length ?? 0) ||
            prevData.drafts?.map((d) => d.draftId).join(',') !==
              newData.drafts?.map((d) => d.draftId).join(',');

          const structuralChange =
            statusChanged || clipStatusChanged || clipsCountChanged || draftsChanged;

          // If content just entered a terminal state, use fresh URLs everywhere
          const isTerminal =
            newData.status === 'ready' || newData.status === 'error';

          if (structuralChange && isTerminal) {
            // Terminal transition — full refresh with new URLs
            return { data: newData, loading: false, error: null };
          }

          // Build merged object: always take new structural data, but
          // preserve old download URLs when they already existed.
          const merged = { ...newData };

          if (!structuralChange) {
            // Preserve top-level download URLs (pre-signed S3 URLs that change
            // on every API call even though the underlying object hasn't changed)
            if (prevData.sourceDownloadUrl && newData.sourceDownloadUrl) {
              merged.sourceDownloadUrl = prevData.sourceDownloadUrl;
            }
            if (prevData.denoisedDownloadUrl && newData.denoisedDownloadUrl) {
              merged.denoisedDownloadUrl = prevData.denoisedDownloadUrl;
            }
            if (prevData.thumbnailDownloadUrl && newData.thumbnailDownloadUrl) {
              merged.thumbnailDownloadUrl = prevData.thumbnailDownloadUrl;
            }
          }

          // Preserve clip download URLs for clips that existed before
          if (merged.clipsArray && prevData.clipsArray) {
            merged.clipsArray = merged.clipsArray.map((clip) => {
              const prevClip = prevData.clipsArray?.find(
                (c) => c.platform === clip.platform && c.status === clip.status,
              );
              if (prevClip?.downloadUrl && clip.downloadUrl) {
                return { ...clip, downloadUrl: prevClip.downloadUrl };
              }
              return clip;
            });
          }

          // Preserve draft media download URLs for drafts that existed before
          if (merged.drafts && prevData.drafts) {
            merged.drafts = merged.drafts.map((draft) => {
              const prevDraft = prevData.drafts?.find(
                (d) => d.draftId === draft.draftId,
              );
              if (
                prevDraft?.mediaDownloadUrls &&
                draft.mediaDownloadUrls &&
                prevDraft.mediaDownloadUrls.length === draft.mediaDownloadUrls.length
              ) {
                return { ...draft, mediaDownloadUrls: prevDraft.mediaDownloadUrls };
              }
              return draft;
            });
          }

          return { data: merged, loading: false, error: null };
        });
      } else {
        // Non-silent (initial load) — use data as-is
        setState({ data: newData, loading: false, error: null });
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load content';
      if (silent) {
        setState((prev) => ({ ...prev, loading: false, error: message }));
      } else {
        setState({ data: null, loading: false, error: message });
      }
    }
  }, [contentId]);

  useEffect(() => {
    fetchContent(false);
  }, [fetchContent]);

  const refetch = useCallback(() => fetchContent(true), [fetchContent]);

  return { ...state, refetch };
}

// ── Job Status Hook (with polling) ────────────────────────────────

export function useJobStatuses(contentId: string | undefined, enabled: boolean, pollEnabled?: boolean) {
  const [state, setState] = useState<UseApiState<JobStatus[]>>({
    data: null,
    loading: true,
    error: null,
  });

  // Track whether we've done the initial fetch (to show loading only on first load)
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const fetchJobs = useCallback(async (isPolling = false) => {
    if (!contentId) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    // Only show loading spinner on initial fetch, not on polls
    if (!isPolling) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }
    try {
      const { jobs } = await api.getJobStatuses(contentId);
      setState({ data: jobs, loading: false, error: null });
      if (!isPolling) setInitialFetchDone(true);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load job statuses';
      setState((prev) => ({ data: prev.data, loading: false, error: message }));
    }
  }, [contentId]);

  // Initial fetch
  useEffect(() => {
    if (enabled && contentId) {
      setInitialFetchDone(false);
      fetchJobs(false);
    } else {
      setState({ data: null, loading: false, error: null });
    }
  }, [fetchJobs, enabled, contentId]);

  // Polling: every 5 seconds while there are active jobs (and polling is enabled)
  useEffect(() => {
    if (pollEnabled === false) return;
    if (!enabled || !contentId || !state.data || !initialFetchDone) return;

    const hasActiveJobs = state.data.some(
      (j) => j.status === 'queued' || j.status === 'running' || j.status === 'pending',
    );

    if (!hasActiveJobs) return;

    const interval = setInterval(() => fetchJobs(true), 5_000);
    return () => clearInterval(interval);
  }, [pollEnabled, enabled, contentId, state.data, fetchJobs, initialFetchDone]);

  return { ...state, refetch: fetchJobs };
}

// ── Drafts Hooks ──────────────────────────────────────────────────

export function useDraftsList(params?: {
  platform?: string;
  status?: string;
  contentId?: string;
}) {
  const [state, setState] = useState<UseApiState<PaginatedResponse<Draft>>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchDrafts = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await api.listDrafts(params);
      setState({ data, loading: false, error: null });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load drafts';
      setState({ data: null, loading: false, error: message });
    }
  }, [params?.platform, params?.status, params?.contentId]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  return { ...state, refetch: fetchDrafts };
}

// ── Draft Actions ─────────────────────────────────────────────────

export function useDraftActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approve = useCallback(async (draftId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.approveDraft(draftId);
      setLoading(false);
      return result;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to approve draft';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const reject = useCallback(async (draftId: string, notes: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.rejectDraft(draftId, notes);
      setLoading(false);
      return result;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to reject draft';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const schedule = useCallback(async (draftId: string, scheduledAt: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.scheduleDraft(draftId, scheduledAt);
      setLoading(false);
      return result;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to schedule draft';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const update = useCallback(async (draftId: string, data: { textContent?: string; platformMetadata?: Record<string, unknown> }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.updateDraft(draftId, data);
      setLoading(false);
      return result;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update draft';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  const deleteDraft = useCallback(async (draftId: string) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteDraft(draftId);
      setLoading(false);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete draft';
      setError(message);
      setLoading(false);
      throw err;
    }
  }, []);

  return { approve, reject, schedule, update, deleteDraft, loading, error };
}

// ── Platforms Hook ─────────────────────────────────────────────────

export function usePlatforms() {
  const [state, setState] = useState<UseApiState<PlatformConfig[]>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    (async () => {
      try {
        const { platforms } = await api.listPlatforms();
        setState({ data: platforms, loading: false, error: null });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Failed to load platforms';
        setState({ data: null, loading: false, error: message });
      }
    })();
  }, []);

  return state;
}

// ── Caption Templates ────────────────────────────────────────────
//
// Proxies Captions.ai's /videos/captions/templates catalog. Cached in-module
// across the SPA session — the catalog changes rarely and every mount
// otherwise triggers a Lambda cold-fetch.

export function useCaptionTemplates() {
  const [state, setState] = useState<UseApiState<CaptionTemplate[]>>({
    data: captionTemplatesCache,
    loading: captionTemplatesCache === null,
    error: null,
  });

  useEffect(() => {
    if (captionTemplatesCache !== null) return;
    let cancelled = false;

    (async () => {
      try {
        const { templates } = await api.listCaptionTemplates();
        if (cancelled) return;
        captionTemplatesCache = templates;
        setState({ data: templates, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Failed to load caption templates';
        setState({ data: null, loading: false, error: message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
